<script setup>
import {ref, inject, onMounted, reactive, watch, computed} from "vue";
import UserActivityItem from "@/views/enterprise/home/aside/updates/users/UserActivityItem.vue";
import Loader from "@/components/ui/Loader.vue";
import ScrolledBottom from "@/components/ScrolledBottom.vue";
import AddNote from "@/views/trackers/tracker/AddNote.vue";
import trackersReportsService from "@/services/trackers-reports.service";
import fileHelper from "@/helpers/file";
import ComparisonReportResultModal from "@/components/trackers/comparisonReport/ComparisonReportResultModal.vue";
import SideBarUpdatesNote from "@/views/trackers/tracker/SideBarUpdatesNote.vue";
import trackersService from "@/services/trackers.service";
import {useStore} from "vue2-helpers/vuex";
import {trackerTypes} from "@/constants/trackers";
import {useRoute} from "vue2-helpers/vue-router";

const store = useStore();
const route = useRoute();

const tracker = inject('tracker');
const trackerId = tracker.value.id;

const maxDate = ref(null);
const actionLogs = ref([]);
const loading = ref(false);
const addNoteModal = ref(null);
const comparisonReportResultModal = ref(null);
const filtersDropdown = ref(null);

const filters = reactive({
    'added-startups': true,
    'removed-startups': true,
    'added-ecosystems': true,
    'removed-ecosystems': true,
    'reviews': true,
    'changed-stage': true,
    'changed-poc-status': true,
    'tasks': true,
    'notes': true,
    'insights': true
});

const filtersLabels = {
    'tracker-updates': 'Tracker Updates',
    'added-startups': 'Added Startups',
    'removed-startups': 'Removed Startups',
    'added-ecosystems': 'Added Ecosystems',
    'removed-ecosystems': 'Removed Ecosystems',
    'notes': 'Notes',
    'tasks': 'Tasks',
    'reviews': 'Reviews',
    'changed-stage': 'Changed Stage',
    'changed-poc-status': 'Changed POC Status',
    'insights': 'Insights'
};

const trackerUpdatesFilters = [
    'added-startups',
    'removed-startups',
    'added-ecosystems',
    'removed-ecosystems',
    'notes',
    'tasks',
    'reviews',
    'changed-stage',
    'changed-poc-status',
];

const hasUpdates = ref(true);
const hasMore = ref(true);

const trackerUpdates = computed({
    get() {
        return trackerUpdatesFilters.every((filter) => filters[filter]);
    },
    set(value) {
        if (value) {
            trackerUpdatesFilters.forEach((filter) => {
                filters[filter] = true;
            });
        } else {
            trackerUpdatesFilters.forEach((filter) => {
                filters[filter] = false;
            });
        }
    }
})

watch(() => store.state.needUpdateTrackerLogs, (value => {
    if (value) {
        reloadActionLogs();
    }
}));

watch(filters, (value => {
    if (value) {
        reloadActionLogs();
    }
}), {deep: true});

onMounted(() => {
    getNotFilteredLogs();

    if (route.query.show_report) {
        comparisonReportResultModal.value.open(route.query.show_report, tracker.value.name);
    }
});

async function getNotFilteredLogs() {
    await getActionLogs();
    hasUpdates.value = !!actionLogs.value.length;
}

async function getActionLogs() {
    loading.value = true;

    let filtersForServer = {
        'types': Object.keys(filters).filter(key => filters[key]),
    };

    const {data} = await trackersService.getLastActionLogs(trackerId, filtersForServer, maxDate.value);

    actionLogs.value = [...actionLogs.value, ...data.logs];
    maxDate.value = data.max_date;
    loading.value = false;
    hasMore.value = data.logs.length >= 10;
}

function reloadActionLogs() {
    actionLogs.value = [];
    maxDate.value = null;
    hasMore.value = true;
    getActionLogs(true);
}

function scrolledBottom() {
    if (loading.value || !hasMore.value) {
        return;
    }

    getActionLogs();
}

async function showReport(log) {
    if (log.name === 'Feature Comparison Report') {
        comparisonReportResultModal.value.open(log.id, tracker.value.name);
    } else {
        const {data} = await trackersReportsService.downloadReport(log.id);

        let currentDate = new Date();
        fileHelper.download(data, `${tracker.value.name}-${currentDate.toLocaleDateString()}.pdf`);
    }
}

async function deleteNote(id) {
    if (!confirm('Delete update?')) {
        return;
    }

    await trackersService.deleteNote(id);
    reloadActionLogs();
}

</script>

<template>
    <div class="update-section">
        <header class="update-section__header">
            <div class="update-section__toggle">
                <b-dropdown text="Updates & Insights" id="dropdown-form" ref="filtersDropdown" class="">
                    <b-dropdown-form>
                        <b-form-group>
                            <div class="update-section__checkbox">
                                <div class="input-checkbox">
                                    <input
                                        type="checkbox"
                                        v-model="trackerUpdates"
                                        id="filter-tracker-updates"
                                    />

                                    <label for="filter-tracker-updates">
                                        Tracker Updates
                                    </label>
                                </div>

                                <template
                                    v-for="type in trackerUpdatesFilters"
                                    class="input-checkbox input-checkbox--inner"
                                >
                                    <div v-if="
                                        !(tracker.type === trackerTypes.ECOSYSTEMS && ['added-startups','removed-startups','changed-stage','changed-poc-status'].includes(type))
                                        && !(tracker.type !== trackerTypes.ECOSYSTEMS && ['added-ecosystems','removed-ecosystems'].includes(type))
                                        && !(tracker.type === trackerTypes.POC && ['changed-stage'].includes(type))
                                        && !(tracker.type !== trackerTypes.POC && ['changed-poc-status'].includes(type))
                                    "
                                         class="input-checkbox input-checkbox--inner" :key="type">
                                        <input
                                            type="checkbox"
                                            v-model="filters[type]"
                                            :id="`filter-${type}`"
                                        />

                                        <label :for="`filter-${type}`">
                                            {{ filtersLabels[type] }}
                                        </label>
                                    </div>
                                </template>

                                <div class="input-checkbox">
                                    <input
                                        type="checkbox"
                                        v-model="filters['insights']"
                                        id="filter-insights"
                                    />

                                    <label for="filter-insights">
                                        AI Insights
                                    </label>
                                </div>
                            </div>

                            <div class="text-center">
                                <button
                                    type="button"
                                    class="button button--bordered"
                                    @click="filtersDropdown.hide(true)"
                                >
                                    Done
                                </button>
                            </div>
                        </b-form-group>
                    </b-dropdown-form>
                </b-dropdown>
            </div>

            <a @click="addNoteModal.show()" class="update-section__add">Note</a>
        </header>

        <div class="sidebar-manage__plate">
            <div
                v-if="tracker.type !== trackerTypes.ECOSYSTEMS"
                id="unapplied-startups-block"
                class="sidebar-manage__plate__colored"
            />

            <div class="sidebar-manage__plate__inner">
                <Loader
                    v-if="loading"
                    :loading="true"
                    style="margin-top: 20px;"
                />

                <ul v-if="actionLogs.length" class="list-insights">
                    <li v-for="(log, index) in actionLogs" :key="index">
                        <SideBarUpdatesNote
                            v-if="log.note?.id"
                            :log="log"
                            @delete="deleteNote"
                        />

                        <UserActivityItem
                            v-else
                            :log="log"
                            :for-tracker="true"
                            @showReport="showReport"
                        />
                    </li>
                </ul>

                <template v-else>
                    <div v-if="hasUpdates" class="sidebar-manage__noupdates">
                        <h6 class="heading6 sidebar-manage__noupdates__title">No updates</h6>
                        <p>Change your selected activity types to see more</p>
                    </div>

                    <div v-else class="sidebar-manage__noupdates">
                        <h6 class="heading6 sidebar-manage__noupdates__title">No updates yet</h6>
                        <p>You'll be able to view startups added and removed, notes, ratings, and more here</p>
                    </div>
                </template>

                <ScrolledBottom v-if="hasMore" @scrolledBottom = scrolledBottom />
            </div>
        </div>

        <AddNote
            ref="addNoteModal"
            @saved="reloadActionLogs()"
        />

        <ComparisonReportResultModal ref="comparisonReportResultModal"/>
    </div>
</template>

<style scoped>

</style>
